import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Auswertungen, ErgebnisseEinzel, Kalender, Mitteilungen } from '../api/apiClient';
import { useMeineMannschaften } from '../hooks/useMeineMannschaften';
import { formatDatum, formatZahl } from '../utils/format';
import { formatWettkampfart, getWettkampfartSchusszahlKey } from '../utils/wettkampfart';
import { istVergangen } from '../utils/kalender';
import AppInstallHinweis from '../components/AppInstallHinweis';
import AusstehendeSignaturenListe from '../components/AusstehendeSignaturenListe';
import ErgebnisHistorieChart from '../components/ErgebnisHistorieChart';
import KalenderEintragKarte from '../components/KalenderEintragKarte';
import { Pagination, SortableTh } from '../components/TableHelpers';
import useDocumentTitle from '../hooks/useDocumentTitle';

/** Wie viele bevorstehende Kalendereinträge im Dashboard-Ausschnitt stehen. */
const NAECHSTE_TERMINE_ANZAHL = 3;
/** Seitengröße der „Letzte Ergebnisse"-Tabelle. */
const ERGEBNISSE_PRO_SEITE = 10;

// Feste, nie rotierende Farbreihenfolge für die Wettkampfarten-Serien im
// Ergebnisverlauf – Slot 1 ist die Markenfarbe (App-Identität bei nur einer
// Serie), Slots 2–4 gegen den hellen Kartenhintergrund validiert
// (dataviz-Skill: CVD-Trennschärfe + Normalsicht-Schwelle bestanden).
const SERIES_COLORS = ['#246b30', '#2a78d6', '#eb6834', '#4a3aa7'];
const SERIES_COLOR_FALLBACK = '#64748b';

function TargetIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  );
}
function TrophyIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}
function TeamIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** Klapp-Pfeil für die mobile „Letzte Ergebnisse"-Karte – ab md immer verborgen, da dort nicht eingeklappt wird. */
function ChevronIcon({ open }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 md:hidden ${open ? '' : '-rotate-90'}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** Anzeigename einer Klasse aus einer Auswertungszeile (Einzel- oder Mannschaftsauswertung – gleiche DTO-Form). */
function buildAuswertungLabel(entry) {
  return entry?.klasse?.klassenName || `Klasse ${entry?.klasse?.klassenId ?? '?'}`;
}

/** Mannschaftsname einer Mannschaftsauswertungszeile – dieselbe Schreibweise wie useMeineMannschaften. */
function buildMannschaftsname(entry) {
  const vereinsName = entry?.verein?.name ?? entry?.verein?.ort ?? `Verein ${entry?.verein?.vereinsId ?? entry?.mannschaft?.vereinsId ?? '?'}`;
  const nummer = entry?.mannschaft?.mannschaftsnummer;
  return nummer != null && nummer > 0 ? `${vereinsName} ${nummer}` : vereinsName;
}

/** Link auf die Auswertungsseite (Mannschafts- und Einzelwertung) der Klasse einer Auswertungszeile. */
function buildAuswertungLink(saisonId, entry) {
  const klassenId = entry?.klasse?.klassenId;
  return saisonId != null && klassenId != null ? `/saison/${saisonId}/klasse/${klassenId}` : null;
}

/** Link auf die Ergebnisseite des Wettkampfs, zu dem ein Einzelergebnis gehört. */
function buildWettkampfLink(ergebnis) {
  return ergebnis?.saisonId != null && ergebnis?.nummer != null
    ? `/saison/${ergebnis.saisonId}/wettkampf/${ergebnis.nummer}/ergebnis`
    : null;
}

/** Kachel; mit `to` wird sie zum Link auf die dahinterliegende Seite. */
function StatTile({ icon, label, value, subtitle, tone = 'slate', dotColor, to }) {
  const toneClasses = {
    slate: 'border-slate-200 bg-white text-slate-500',
    brand: 'border-brand-200 bg-brand-50 text-brand-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
  }[tone];
  const className = `block rounded-lg border px-4 py-3 ${toneClasses}${to ? ' no-underline transition-shadow hover:shadow-md' : ''}`;
  const inhalt = (
    <>
      <div className="flex items-center gap-1.5">
        {dotColor ? <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} /> : icon}
        <span className="text-[10px] uppercase tracking-wider truncate">{label}</span>
      </div>
      <div className="text-xl font-semibold text-slate-900 mt-1">{value}</div>
      {subtitle && <div className="text-xs text-slate-400 mt-0.5 truncate">{subtitle}</div>}
    </>
  );
  return to
    ? <Link to={to} className={className}>{inhalt}</Link>
    : <div className={className}>{inhalt}</div>;
}

/** Zeile einer Platzierungsliste; mit `to` ist die ganze Zeile ein Link zur Auswertung. */
function PlatzierungZeile({ to, children }) {
  if (!to) return <li className="px-4 py-3">{children}</li>;
  return (
    <li>
      <Link to={to} className="block px-4 py-3 no-underline transition-colors hover:bg-slate-50">{children}</Link>
    </li>
  );
}

// Medaillenfarben für die ersten drei Plätze – Gold/Silber/Bronze,
// alles darunter bleibt beim neutralen Standard-Badge.
const PLATZ_MEDAILLEN_CLASSES = {
  1: 'badge bg-amber-100 text-amber-800',
  2: 'badge bg-slate-200 text-slate-700',
  3: 'badge bg-orange-100 text-orange-800',
};

function PlatzBadge({ platzierung, total }) {
  const className = PLATZ_MEDAILLEN_CLASSES[platzierung] ?? 'badge badge-gray';
  return (
    <span className={`${className} shrink-0`}>
      {platzierung != null ? `${platzierung}.${total ? ` / ${total}` : ''}` : '–'}
    </span>
  );
}

function DashboardPage({ currentUser }) {
  useDocumentTitle('Mein Dashboard');
  const navigate = useNavigate();
  const schuetzenId = Number(currentUser?.schuetzenId);

  const { loading: teamsLoading, activeSaison, teams } = useMeineMannschaften(schuetzenId, currentUser);

  const [historie, setHistorie] = useState([]);
  const [historieLoading, setHistorieLoading] = useState(true);
  const [historieError, setHistorieError] = useState(null);

  const [einzelauswertung, setEinzelauswertung] = useState([]);
  const [mannschaftsauswertung, setMannschaftsauswertung] = useState([]);
  const [auswertungLoading, setAuswertungLoading] = useState(false);

  const [termine, setTermine] = useState([]);
  const [termineLoading, setTermineLoading] = useState(true);
  const [termineFehler, setTermineFehler] = useState(null);
  const [antwortendeId, setAntwortendeId] = useState(null);

  // Zeitraum-Filter für Ergebnisverlauf und Letzte Ergebnisse: „ab Saison X".
  // null = noch nicht initialisiert, '' = Nutzer hat bewusst "Alle" gewählt.
  // Voreinstellung ist immer die Vorsaison der aktiven Saison – sobald die
  // bekannt ist, wird einmalig vorbelegt, spätere Auswahl bleibt unangetastet.
  const [abSaisonId, setAbSaisonId] = useState(null);
  useEffect(() => {
    const saisonId = Number(activeSaison?.saisonId);
    if (abSaisonId === null && Number.isFinite(saisonId)) {
      setAbSaisonId(String(saisonId - 1));
    }
  }, [activeSaison, abSaisonId]);

  useEffect(() => {
    let cancelled = false;
    setTermineLoading(true);
    Kalender.getMeine()
      .then((data) => { if (!cancelled) setTermine(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) setTermineFehler(err.message || 'Fehler beim Laden der Termine.'); })
      .finally(() => { if (!cancelled) setTermineLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleTerminAntwort = async (eintrag, antwort) => {
    setAntwortendeId(eintrag.id);
    setTermineFehler(null);
    try {
      await Mitteilungen.antworten(eintrag.termin.mitteilungsId, antwort);
      const data = await Kalender.getMeine();
      setTermine(Array.isArray(data) ? data : []);
    } catch (err) {
      setTermineFehler(err.message || 'Antwort konnte nicht gespeichert werden.');
    } finally {
      setAntwortendeId(null);
    }
  };

  const naechsteTermine = useMemo(
    () => termine.filter((e) => !istVergangen(e)).slice(0, NAECHSTE_TERMINE_ANZAHL),
    [termine],
  );

  useEffect(() => {
    let cancelled = false;
    if (!Number.isFinite(schuetzenId) || schuetzenId <= 0) {
      setHistorie([]);
      setHistorieLoading(false);
      return () => { cancelled = true; };
    }
    setHistorieLoading(true);
    setHistorieError(null);
    ErgebnisseEinzel.getBySchuetze(schuetzenId)
      .then((data) => { if (!cancelled) setHistorie(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) setHistorieError(err.message || 'Fehler beim Laden der Ergebnisse.'); })
      .finally(() => { if (!cancelled) setHistorieLoading(false); });
    return () => { cancelled = true; };
  }, [schuetzenId]);

  useEffect(() => {
    let cancelled = false;
    const saisonId = Number(activeSaison?.saisonId);
    if (!Number.isFinite(schuetzenId) || schuetzenId <= 0 || !Number.isFinite(saisonId)) {
      setEinzelauswertung([]);
      setMannschaftsauswertung([]);
      return () => { cancelled = true; };
    }
    setAuswertungLoading(true);
    Promise.all([
      Auswertungen.getEinzelBySchuetzeUndSaison(schuetzenId, saisonId).catch(() => []),
      Auswertungen.getMannschaftBySaison(saisonId).catch(() => []),
    ]).then(([einzel, mannschaft]) => {
      if (cancelled) return;
      setEinzelauswertung(Array.isArray(einzel) ? einzel : []);
      setMannschaftsauswertung(Array.isArray(mannschaft) ? mannschaft : []);
    }).finally(() => { if (!cancelled) setAuswertungLoading(false); });
    return () => { cancelled = true; };
  }, [schuetzenId, activeSaison]);

  // Eigene Mannschaftsplatzierungen: aus der gesamten Saisonauswertung auf die
  // eigenen Mannschafts-IDs filtern, Gesamtzahl der Mannschaften der jeweiligen
  // Klasse für die Anzeige „3. / 12" mitzählen.
  const meineMannschaftsplatzierungen = useMemo(() => {
    const eigeneIds = new Set(teams.map((t) => Number(t.mannschaftsId)));
    if (eigeneIds.size === 0) return [];
    const totalByKlasse = new Map();
    mannschaftsauswertung.forEach((e) => {
      const kId = e?.klasse?.klassenId;
      if (kId == null) return;
      totalByKlasse.set(kId, (totalByKlasse.get(kId) ?? 0) + 1);
    });
    return mannschaftsauswertung
      .filter((e) => eigeneIds.has(Number(e?.mannschaft?.mannschaftsId)))
      .map((e) => ({ ...e, totalInKlasse: totalByKlasse.get(e?.klasse?.klassenId) ?? null }))
      .sort((a, b) => (a.platzierung ?? Number.MAX_SAFE_INTEGER) - (b.platzierung ?? Number.MAX_SAFE_INTEGER));
  }, [mannschaftsauswertung, teams]);

  // Saisons, die in der Historie vorkommen – für die Auswahl des Zeitraums,
  // unabhängig von der aktuellen Filterwahl.
  const verfuegbareSaisons = useMemo(() => {
    const ids = new Set(historie.map((e) => Number(e.saisonId)).filter(Number.isFinite));
    return [...ids].sort((a, b) => b - a);
  }, [historie]);

  const historieGefiltert = useMemo(() => {
    if (!abSaisonId) return historie;
    const grenze = Number(abSaisonId);
    return historie.filter((e) => Number(e.saisonId) >= grenze);
  }, [historie, abSaisonId]);

  // Ergebnisse nach Wettkampfart + Schusszahl gruppieren – unterschiedliche
  // Ringgrenzen (z. B. 300 bei KK, 400 bei LG, 30 vs. 40 Schuss) bekommen
  // jeweils eine eigene Serie im Diagramm, alle auf derselben Ringe-Achse.
  const wettkampfartGroups = useMemo(() => {
    const map = new Map();
    historieGefiltert.forEach((e) => {
      const key = getWettkampfartSchusszahlKey({ wettkampfart: e?.wettkampfart }) || 'sonstige';
      const group = map.get(key) ?? {
        key,
        label: [formatWettkampfart({ wettkampfart: e?.wettkampfart }), e?.wettkampfart?.schusszahl ? `${e.wettkampfart.schusszahl} Schuss` : null]
          .filter(Boolean).join(' · ') || 'Sonstige',
        entries: [],
      };
      group.entries.push(e);
      map.set(key, group);
    });
    // Feste Reihenfolge nach frühestem Ergebnis – Farbzuordnung bleibt so über
    // die Session stabil und hängt nicht von der (variablen) Trefferzahl ab.
    return [...map.values()]
      .sort((a, b) => String(a.entries[0]?.datum ?? '').localeCompare(String(b.entries[0]?.datum ?? '')))
      .map((g, idx) => ({ ...g, color: SERIES_COLORS[idx] ?? SERIES_COLOR_FALLBACK }));
  }, [historieGefiltert]);

  const chartSeries = useMemo(() => wettkampfartGroups.map((g) => ({
    key: g.key,
    label: g.label,
    color: g.color,
    data: g.entries.map((e) => ({
      datum: e.datum,
      ringe: e.ringe,
      subtitle: [e.klassenName, e.nummer != null ? `Wettkampf Nr. ${e.nummer}` : null].filter(Boolean).join(' · '),
    })),
  })), [wettkampfartGroups]);

  // Bestleistung je Disziplin bleibt bewusst über die gesamte (ungefilterte)
  // Historie berechnet – der persönliche Rekord soll sich nicht ändern, nur
  // weil der Diagramm-Zeitraum enger gewählt wurde. Gruppierung und
  // Farbzuordnung folgen derselben Regel wie wettkampfartGroups, damit die
  // Kacheln zu den Serienfarben im Diagramm passen.
  const bestleistungenProDisziplin = useMemo(() => {
    const map = new Map();
    historie.forEach((e) => {
      const key = getWettkampfartSchusszahlKey({ wettkampfart: e?.wettkampfart }) || 'sonstige';
      const group = map.get(key) ?? { key, erstesDatum: e.datum, beste: e };
      if (String(e.datum ?? '') < String(group.erstesDatum ?? '')) group.erstesDatum = e.datum;
      if (Number(e.ringe) > Number(group.beste.ringe)) group.beste = e;
      map.set(key, group);
    });
    return [...map.values()]
      .sort((a, b) => String(a.erstesDatum ?? '').localeCompare(String(b.erstesDatum ?? '')))
      .map((g, idx) => {
        const label = [formatWettkampfart({ wettkampfart: g.beste?.wettkampfart }), g.beste?.wettkampfart?.schusszahl ? `${g.beste.wettkampfart.schusszahl} Schuss` : null]
          .filter(Boolean).join(' · ') || 'Sonstige';
        return { ...g.beste, key: g.key, wettkampfartLabel: label, color: SERIES_COLORS[idx] ?? SERIES_COLOR_FALLBACK };
      });
  }, [historie]);

  const wettkaempfeAktuelleSaison = useMemo(() => {
    const saisonId = Number(activeSaison?.saisonId);
    if (!Number.isFinite(saisonId)) return 0;
    return historie.filter((e) => Number(e.saisonId) === saisonId).length;
  }, [historie, activeSaison]);

  // Mobil startet die Karte eingeklappt (nur Titel), ab md immer aufgeklappt.
  const [ergebnisseAufgeklappt, setErgebnisseAufgeklappt] = useState(false);
  const [ergebnisSeite, setErgebnisSeite] = useState(1);
  const [ergebnisSort, setErgebnisSort] = useState({ key: 'datum', direction: 'desc' });
  useEffect(() => { setErgebnisSeite(1); }, [abSaisonId, schuetzenId, ergebnisSort]);

  const handleErgebnisSort = (key) => setErgebnisSort((prev) =>
    prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'datum' ? 'desc' : 'asc' }
  );

  const navigateZuWettkampf = (eintrag) => {
    const link = buildWettkampfLink(eintrag);
    if (link) navigate(link);
  };

  const ergebnisseSortiert = useMemo(() => {
    const arr = [...historieGefiltert];
    const { key, direction } = ergebnisSort;
    const richtung = direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      switch (key) {
        case 'klasse':
          cmp = String(a.klassenName ?? '').localeCompare(String(b.klassenName ?? ''), 'de', { sensitivity: 'base', numeric: true });
          break;
        case 'heimAuswaerts':
          cmp = String(a.heimAuswaerts ?? '').localeCompare(String(b.heimAuswaerts ?? ''), 'de', { sensitivity: 'base' });
          break;
        case 'ringe':
          cmp = Number(a.ringe) - Number(b.ringe);
          break;
        case 'datum':
        default:
          cmp = new Date(a.datum).getTime() - new Date(b.datum).getTime();
          break;
      }
      return cmp * richtung;
    });
    return arr;
  }, [historieGefiltert, ergebnisSort]);

  const ergebnisSeitenGesamt = Math.max(1, Math.ceil(ergebnisseSortiert.length / ERGEBNISSE_PRO_SEITE));
  const ergebnisSeiteAktuell = Math.min(ergebnisSeite, ergebnisSeitenGesamt);
  const letzteErgebnisse = ergebnisseSortiert.slice(
    (ergebnisSeiteAktuell - 1) * ERGEBNISSE_PRO_SEITE,
    ergebnisSeiteAktuell * ERGEBNISSE_PRO_SEITE,
  );

  const displayName = [currentUser?.vorname, currentUser?.nachname].filter(Boolean).join(' ') || 'Schütze';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Mein Dashboard</h1>
          <p className="page-subtitle">
            {displayName}
            {activeSaison?.saisonId != null && ` · Saison ${activeSaison.saisonId}/${Number(activeSaison.saisonId) + 1}`}
          </p>
        </div>
      </div>

      {historieError && <div className="alert-error mb-4">{historieError}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <StatTile
          icon={<TargetIcon />}
          label="Wettkämpfe (Saison)"
          value={teamsLoading || historieLoading ? '…' : wettkaempfeAktuelleSaison}
          to={activeSaison?.saisonId != null ? `/saison/${activeSaison.saisonId}` : undefined}
        />
        <StatTile
          icon={<TeamIcon />}
          label="Mannschaften"
          value={teamsLoading ? '…' : teams.length}
          tone="slate"
          to="/meine-mannschaften"
        />
        {historieLoading ? (
          <StatTile icon={<TrophyIcon />} label="Bestleistung" value="…" tone="brand" />
        ) : bestleistungenProDisziplin.length === 0 ? (
          <StatTile icon={<TrophyIcon />} label="Bestleistung" value="–" tone="brand" />
        ) : (
          bestleistungenProDisziplin.map((b) => {
            const mehrereDisziplinen = bestleistungenProDisziplin.length > 1;
            return (
              <StatTile
                key={b.key}
                icon={<TrophyIcon />}
                dotColor={mehrereDisziplinen ? b.color : undefined}
                label={mehrereDisziplinen ? b.wettkampfartLabel : 'Bestleistung'}
                value={formatZahl(b.ringe, 0)}
                subtitle={mehrereDisziplinen ? formatDatum(b.datum) : `${formatDatum(b.datum)} · ${b.wettkampfartLabel}`}
                tone="brand"
                to={buildWettkampfLink(b) ?? undefined}
              />
            );
          })
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          {verfuegbareSaisons.length > 1 && (
            <div className="flex items-center gap-2">
              <label htmlFor="ab-saison" className="text-xs font-medium text-slate-500">Zeitraum</label>
              <select
                id="ab-saison"
                className="field-select w-auto"
                value={abSaisonId ?? ''}
                onChange={(e) => setAbSaisonId(e.target.value)}
              >
                <option value="">Alle Saisons</option>
                {verfuegbareSaisons.map((s) => (
                  <option key={s} value={s}>ab Saison {s}/{s + 1}</option>
                ))}
              </select>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-800">Ergebnisverlauf</h3>
            </div>
            <div className="card-body">
              {historieLoading ? (
                <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><span className="spinner" /> Laden…</div>
              ) : (
                <ErgebnisHistorieChart series={chartSeries} />
              )}
            </div>
          </div>

          <div className="card overflow-hidden">
            <button
              type="button"
              onClick={() => setErgebnisseAufgeklappt((v) => !v)}
              className="card-header w-full text-left hover:bg-slate-50 md:hover:bg-transparent md:pointer-events-none md:cursor-default transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ChevronIcon open={ergebnisseAufgeklappt} />
                <h3 className="font-semibold text-slate-800">Letzte Ergebnisse</h3>
              </div>
            </button>
            <div className={`${ergebnisseAufgeklappt ? '' : 'hidden'} md:block`}>
              <div className="card-body p-0">
                {historieLoading ? (
                  <div className="flex items-center gap-2 text-slate-400 p-4"><span className="spinner" /> Laden…</div>
                ) : letzteErgebnisse.length === 0 ? (
                  <p className="text-slate-400 italic text-sm px-4 py-4">Noch keine abgeschlossenen Wettkämpfe vorhanden.</p>
                ) : (
                  <>
                    {/* Desktop: Tabelle */}
                    <div className="hidden md:block data-table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <SortableTh sortKey="datum" sortConfig={ergebnisSort} onSort={handleErgebnisSort}>Datum</SortableTh>
                            <SortableTh sortKey="klasse" sortConfig={ergebnisSort} onSort={handleErgebnisSort}>Klasse</SortableTh>
                            <SortableTh sortKey="heimAuswaerts" sortConfig={ergebnisSort} onSort={handleErgebnisSort}>Heim/Auswärts</SortableTh>
                            <SortableTh sortKey="ringe" sortConfig={ergebnisSort} onSort={handleErgebnisSort} className="text-right">Ringe</SortableTh>
                          </tr>
                        </thead>
                        <tbody>
                          {letzteErgebnisse.map((e) => (
                            <tr
                              key={e.ergebnisEinzelId}
                              className={e.saisonId != null && e.nummer != null ? 'cursor-pointer hover:bg-slate-50' : undefined}
                              onClick={() => navigateZuWettkampf(e)}
                            >
                              <td>{formatDatum(e.datum)}</td>
                              <td className="text-slate-500">{e.klassenName ?? '-'}</td>
                              <td>
                                {e.heimAuswaerts === 'heim' ? (
                                  <span className="badge badge-blue">Heim</span>
                                ) : e.heimAuswaerts === 'auswaerts' ? (
                                  <span className="badge badge-gray">Auswärts</span>
                                ) : (
                                  <span className="text-slate-300">–</span>
                                )}
                              </td>
                              <td className="text-right font-mono font-semibold">{formatZahl(e.ringe, 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile: Karten */}
                    <div className="md:hidden p-3 space-y-2">
                      {letzteErgebnisse.map((e) => (
                        <div
                          key={e.ergebnisEinzelId}
                          className={`rounded-lg border border-slate-200 p-3 ${e.saisonId != null && e.nummer != null ? 'cursor-pointer active:bg-slate-50' : ''}`}
                          onClick={() => navigateZuWettkampf(e)}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <span className="text-xs text-slate-500">{formatDatum(e.datum)}</span>
                            <span className="font-mono font-semibold text-sm text-slate-900">{formatZahl(e.ringe, 0)} Ringe</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-slate-800">{e.klassenName ?? '-'}</span>
                            {e.heimAuswaerts === 'heim' ? (
                              <span className="badge badge-blue shrink-0">Heim</span>
                            ) : e.heimAuswaerts === 'auswaerts' ? (
                              <span className="badge badge-gray shrink-0">Auswärts</span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {!historieLoading && ergebnisSeitenGesamt > 1 && (
                <div className="border-t border-slate-100 px-4 py-3">
                  <Pagination
                    currentPage={ergebnisSeiteAktuell}
                    totalPages={ergebnisSeitenGesamt}
                    onPageChange={setErgebnisSeite}
                  />
                </div>
              )}
            </div>
          </div>

          <AusstehendeSignaturenListe schuetzenId={schuetzenId} />
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-800">Meine Mannschaften</h3>
            </div>
            <div className="card-body">
              {teamsLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-2"><span className="spinner" /> Laden…</div>
              ) : teams.length === 0 ? (
                <p className="text-sm text-slate-400 italic">Du bist in der aktiven Saison in keiner Mannschaft gemeldet.</p>
              ) : (
                <div className="space-y-2">
                  {teams.map((team) => (
                    <Link
                      key={team.mannschaftsId}
                      to={`/mannschaft/${team.mannschaftsId}`}
                      className="block rounded-lg border border-slate-200 p-3 hover:shadow-md hover:border-slate-300 transition-shadow"
                    >
                      <p className="font-semibold text-slate-900 text-sm">{team.label}</p>
                      {team.klassenName && <p className="text-xs text-slate-500 mt-1">{team.klassenName}</p>}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-800">Meine Einzelplatzierung {activeSaison?.saisonId != null ? `– Saison ${activeSaison.saisonId}` : ''}</h3>
            </div>
            <div className="card-body p-0">
              {auswertungLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 p-4"><span className="spinner" /> Laden…</div>
              ) : einzelauswertung.length === 0 ? (
                <p className="text-sm text-slate-400 italic px-4 py-4">Für die aktuelle Saison liegt noch keine Auswertung vor.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {einzelauswertung.map((entry, idx) => (
                    <PlatzierungZeile key={idx} to={buildAuswertungLink(activeSaison?.saisonId, entry)}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-900">{buildMannschaftsname(entry)}</span>
                        <PlatzBadge platzierung={entry.platzierung} />
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {buildAuswertungLabel(entry)} · Ø {formatZahl(entry.durchschnittsergebnis, 2)} Ringe · {entry.anzahlErgebnisse} {entry.anzahlErgebnisse === 1 ? 'Wettkampf' : 'Wettkämpfe'}
                        {!entry.istQualifiziert && <span className="text-amber-600"> · nicht qualifiziert</span>}
                      </div>
                    </PlatzierungZeile>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-800">Meine Mannschaftsplatzierungen {activeSaison?.saisonId != null ? `– Saison ${activeSaison.saisonId}` : ''}</h3>
            </div>
            <div className="card-body p-0">
              {auswertungLoading || teamsLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 p-4"><span className="spinner" /> Laden…</div>
              ) : meineMannschaftsplatzierungen.length === 0 ? (
                <p className="text-sm text-slate-400 italic px-4 py-4">Für die aktuelle Saison liegt noch keine Mannschaftsauswertung vor.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {meineMannschaftsplatzierungen.map((entry, idx) => (
                    <PlatzierungZeile key={idx} to={buildAuswertungLink(activeSaison?.saisonId, entry)}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-900">{buildMannschaftsname(entry)}</span>
                        <PlatzBadge platzierung={entry.platzierung} total={entry.totalInKlasse} />
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {buildAuswertungLabel(entry)} · {entry.gesamtPunkte}:{entry.gesamtGegenpunkte} Punkte
                        {entry.anzahlWettkaempfe > 0 && ` · Ø ${formatZahl(entry.summeRinge / entry.anzahlWettkaempfe, 1)} Ringe`}
                      </div>
                    </PlatzierungZeile>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="card overflow-hidden">
            <Link to="/allgemein/kalender" className="card-header no-underline transition-colors hover:bg-slate-50">
              <h3 className="font-semibold text-slate-800">Meine Termine</h3>
              <svg className="h-4 w-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </Link>
            {termineFehler && <div className="alert-error mx-4 mt-3">{termineFehler}</div>}
            <div className="card-body space-y-2">
              {termineLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-2"><span className="spinner" /> Laden…</div>
              ) : naechsteTermine.length === 0 ? (
                <p className="text-sm text-slate-400 italic">Keine bevorstehenden Termine.</p>
              ) : (
                naechsteTermine.map((eintrag) => (
                  <KalenderEintragKarte
                    key={eintrag.id}
                    eintrag={eintrag}
                    onAntwort={handleTerminAntwort}
                    antwortend={antwortendeId === eintrag.id}
                    onFehler={setTermineFehler}
                    zeigeEtiketten={false}
                  />
                ))
              )}
            </div>
            <div className="border-t border-slate-100 px-4 py-3">
              <Link to="/allgemein/kalender" className="btn btn-secondary btn-sm w-full justify-center no-underline">
                Weitere Termine
              </Link>
            </div>
          </div>
        </div>
      </div>

      <AppInstallHinweis />
    </div>
  );
}

export default DashboardPage;
